import React, { useState } from "react";
import { useFormState, useWatch } from "react-hook-form";

import { ToneSegments } from "../../../../components/FormPanel";
import {
  AmountCard,
  FormRow,
  SelectSheet,
  SelectSheetOption,
} from "../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  RowsCard,
} from "../../../../components/MobileForm/styles";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
} from "../../../../components/Input";
import { NetPreview } from "../../../../components/NetPreview";
import { NetToGrossHelper } from "../../../../components/NetToGrossHelper";
import {
  amountLabelOf,
  OPTIONAL_TAX_WITHHELD_RULES,
} from "../../../../utils/taxWithheld";
import {
  AMOUNT_RULES,
  SEGMENT_LABELS,
  TONE_BY_TYPE,
  TYPE_OPTIONS,
} from "./constants";
import { MobileProgressFormProps } from "./types";

type ProgressSheet = "category" | "subCategory" | "from" | "to" | null;

/** Compact savings-progress fields: the amount hero with its type segments, plus category / account pickers driven by bottom sheets. */
export const MobileProgressForm: React.FC<MobileProgressFormProps> = ({
  control,
  register,
  setValue,
  clearErrors,
  transactionType,
  accountOptions,
  categoryOptions,
  subCategoryOptions,
  shouldShowCategory,
  shouldShowSubCategory,
  shouldShowFromAccount,
  shouldShowToAccount,
  shouldShowTax,
  isSubmitting,
  onTypeChange,
}) => {
  const [openSheet, setOpenSheet] = useState<ProgressSheet>(null);

  const values = useWatch({ control });
  const { errors } = useFormState({ control });

  // Account fields are picked through bottom sheets rather than Controller
  // inputs, so register them here purely to attach validation; handleSubmit
  // then enforces them on submit.
  if (shouldShowFromAccount) {
    register("fromAccount", { required: "From account is required" });
  }
  if (shouldShowToAccount) {
    register("toAccount", { required: "To account is required" });
  }

  const handleCategorySelect = (option: SelectSheetOption) => {
    const selected = categoryOptions.find(
      (item) => item.value === option.value,
    );
    setValue("category", selected ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("subCategory", null, { shouldDirty: true });
    setOpenSheet(null);
  };

  const handleSubCategorySelect = (option: SelectSheetOption) => {
    const selected = subCategoryOptions.find(
      (item) => item.value === option.value,
    );
    setValue("subCategory", selected ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setOpenSheet(null);
  };

  const handleFromSelect = (option: SelectSheetOption) => {
    setValue("fromAccount", option, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors("fromAccount");
    setOpenSheet(null);
  };

  const handleToSelect = (option: SelectSheetOption) => {
    setValue("toAccount", option, { shouldDirty: true, shouldValidate: true });
    clearErrors("toAccount");
    setOpenSheet(null);
  };

  const showRows =
    shouldShowCategory ||
    shouldShowSubCategory ||
    shouldShowFromAccount ||
    shouldShowToAccount;

  return (
    <>
      <AmountCard
        id="mobile-savings-progress-amount"
        label={amountLabelOf(shouldShowTax, { required: true })}
        control={control}
        rules={AMOUNT_RULES}
        tone={TONE_BY_TYPE[transactionType.value]}
        disabled={isSubmitting}
        required
      >
        <ToneSegments
          groupLabel="Type"
          options={TYPE_OPTIONS}
          toneByValue={TONE_BY_TYPE}
          labelByValue={SEGMENT_LABELS}
          selectedValue={transactionType.value}
          disabled={isSubmitting}
          onChange={onTypeChange}
        />
      </AmountCard>

      {shouldShowTax && (
        <Card>
          <CardLabel htmlFor="mobile-savings-progress-tax">
            Tax withheld
          </CardLabel>
          <FormMoneyInput
            id="mobile-savings-progress-tax"
            name="taxWithheld"
            control={control}
            rules={OPTIONAL_TAX_WITHHELD_RULES}
            disabled={isSubmitting}
            placeholder="Enter Tax Withheld"
          />
          <NetPreview control={control} />
          <NetToGrossHelper setValue={setValue} disabled={isSubmitting} />
        </Card>
      )}

      <Card>
        <CardLabel htmlFor="mobile-savings-progress-date">Date *</CardLabel>
        <InputDate
          id="mobile-savings-progress-date"
          name="transactionDate"
          control={control}
          rules={{ required: "Date is required!" }}
          maxDate={new Date()}
          placeholder="YYYY/MM/DD"
          disabled={isSubmitting}
        />
      </Card>

      {showRows && (
        <RowsCard>
          {shouldShowCategory && (
            <FormRow
              label="Category"
              value={values.category?.label}
              placeholder="Select category"
              onClick={() => setOpenSheet("category")}
            />
          )}
          {shouldShowSubCategory && (
            <FormRow
              label="Subcategory"
              value={values.subCategory?.label}
              placeholder="Select subcategory"
              onClick={() => setOpenSheet("subCategory")}
            />
          )}
          {shouldShowFromAccount && (
            <FormRow
              label="From account"
              value={values.fromAccount?.label}
              placeholder="Select account"
              error={errors.fromAccount?.message}
              onClick={() => setOpenSheet("from")}
            />
          )}
          {shouldShowToAccount && (
            <FormRow
              label="To account"
              value={values.toAccount?.label}
              placeholder="Select account"
              error={errors.toAccount?.message}
              onClick={() => setOpenSheet("to")}
            />
          )}
        </RowsCard>
      )}

      <Card>
        <CardLabel htmlFor="mobile-savings-progress-note">
          Description
        </CardLabel>
        <FormInput
          id="mobile-savings-progress-note"
          name="note"
          control={control}
          rules={{
            minLength: {
              value: 3,
              message: "Description must be at least 3 characters",
            },
            maxLength: {
              value: 255,
              message: "Description cannot exceed 255 characters",
            },
          }}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="Enter Description"
        />
      </Card>

      <SelectSheet
        isOpen={openSheet === "category"}
        title="Select category"
        options={categoryOptions}
        selectedValue={values.category?.value}
        onSelect={handleCategorySelect}
        onClose={() => setOpenSheet(null)}
      />
      <SelectSheet
        isOpen={openSheet === "subCategory"}
        title="Select subcategory"
        options={subCategoryOptions}
        selectedValue={values.subCategory?.value}
        onSelect={handleSubCategorySelect}
        onClose={() => setOpenSheet(null)}
      />
      <SelectSheet
        isOpen={openSheet === "from"}
        title="Select from account"
        options={accountOptions}
        selectedValue={values.fromAccount?.value}
        onSelect={handleFromSelect}
        onClose={() => setOpenSheet(null)}
      />
      <SelectSheet
        isOpen={openSheet === "to"}
        title="Select to account"
        options={accountOptions}
        selectedValue={values.toAccount?.value}
        onSelect={handleToSelect}
        onClose={() => setOpenSheet(null)}
      />
    </>
  );
};
